/**
 * CustomerDocumentsTab - Aba de documentos no modal de cliente
 * Exibe documentos em grid de thumbnails ou lista com detalhes
 * Usa AttachmentThumbnail (padrão compartilhado com Deal Modal)
 * 
 * @version 5.0.0
 * RELEASE: 2026-04-09 v5.0.0 - Toggle Miniatura/Lista
 */
import { useState, useRef } from 'react';
import {
  FileCheck,
  Upload,
  Loader2,
  LayoutGrid,
  List,
  Eye,
  Download,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useCustomerDocuments } from '@/hooks/useCustomerDocuments';
import { useDocumentTypes } from '@/hooks/useDocumentTypes';
import { toast } from 'sonner';
import { AttachmentThumbnail, type BaseAttachment } from '@/components/shared/AttachmentThumbnail';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { getMimeMediaType, getFileIcon, getFileColor, formatFileSize } from '@/lib/file-utils';

/**
 * Converte mime_type para file_type compatível com AttachmentThumbnail
 * Mapeia para: image, pdf, document, spreadsheet, other
 */
function mimeToFileType(mimeType: string | null): string {
  if (!mimeType) return 'other';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv') return 'spreadsheet';
  if (mimeType.includes('word') || mimeType.includes('document') || mimeType === 'text/plain') return 'document';
  return 'other';
}

interface CustomerDocumentsTabProps {
  customerId: string;
  isActive?: boolean;
}

/**
 * Aba de documentos do modal de cliente
 * Suporta visualização em miniatura (grid) e lista (detalhada)
 */
export function CustomerDocumentsTab({
  customerId,
  isActive = true,
}: CustomerDocumentsTabProps) {
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'thumbnail' | 'list'>('thumbnail');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estado do lightbox para preview interno
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState<MediaItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Hooks de dados
  const {
    documents,
    isLoading,
    uploadDocument,
    deleteDocument,
    getPublicUrl,
  } = useCustomerDocuments(customerId, isActive);

  const { documentTypes } = useDocumentTypes();

  // Handler para selecionar tipo e abrir seletor de arquivo
  const handleUploadClick = () => {
    if (!selectedTypeId) {
      toast.error('Selecione o tipo de documento primeiro');
      return;
    }
    fileInputRef.current?.click();
  };

  // Handler do input file
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTypeId) return;

    await uploadDocument.mutateAsync({
      file,
      document_type_id: selectedTypeId,
    });

    setSelectedTypeId('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /**
   * Abre preview interno via ImageLightbox para todos os tipos
   */
  const handleView = (doc: typeof documents[0]) => {
    const url = getPublicUrl(doc.storage_path);
    if (!url) return;

    const items: MediaItem[] = documents.map((d) => ({
      url: getPublicUrl(d.storage_path) || '',
      type: getMimeMediaType(d.mime_type) as 'image' | 'video' | 'document',
      title: d.file_name,
    }));

    const idx = documents.findIndex((d) => d.id === doc.id);
    setLightboxItems(items);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  };

  // Confirmar exclusão
  const handleConfirmDelete = () => {
    if (deleteDocId) {
      deleteDocument.mutate(deleteDocId, {
        onSuccess: () => setDeleteDocId(null),
      });
    }
  };

  return (
    <div className="h-full overflow-y-auto space-y-4">
      {/* Input file oculto */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
      />

      {/* Barra de upload + toggle de visualização */}
      <div className="flex items-center gap-2">
        <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
          <SelectTrigger className="flex-1 h-9 text-sm">
            <SelectValue placeholder="Tipo de documento..." />
          </SelectTrigger>
          <SelectContent>
            {documentTypes
              .filter((dt) => dt.is_active)
              .map((dt) => (
                <SelectItem key={dt.id} value={dt.id}>
                  {dt.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          onClick={handleUploadClick}
          disabled={!selectedTypeId || uploadDocument.isPending}
          className="gap-1.5"
        >
          {uploadDocument.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          Enviar
        </Button>

        {/* Toggle Miniatura / Lista */}
        <ToggleGroup
          type="single"
          value={viewMode}
          onValueChange={(v) => v && setViewMode(v as 'thumbnail' | 'list')}
          variant="outline"
          size="sm"
        >
          <ToggleGroupItem value="thumbnail" aria-label="Visualizar em miniatura" className="h-8 w-8 p-0">
            <LayoutGrid className="h-3.5 w-3.5" />
          </ToggleGroupItem>
          <ToggleGroupItem value="list" aria-label="Visualizar em lista" className="h-8 w-8 p-0">
            <List className="h-3.5 w-3.5" />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Conteúdo: loading, vazio, ou documentos */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : documents.length === 0 ? (
        <div className="py-8 text-center">
          <FileCheck className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Nenhum documento anexado</p>
          <p className="text-xs text-muted-foreground mt-1">
            Selecione um tipo e envie o primeiro documento
          </p>
        </div>
      ) : viewMode === 'thumbnail' ? (
        /* ===== MODO MINIATURA (Grid) ===== */
        <div className="flex gap-2 flex-wrap">
          {documents.map((doc) => {
            const publicUrl = getPublicUrl(doc.storage_path) || '';
            const attachment: BaseAttachment = {
              id: doc.id,
              file_name: doc.file_name,
              file_type: mimeToFileType(doc.mime_type),
              public_url: publicUrl,
              thumbnail_url: doc.mime_type?.startsWith('image/') ? publicUrl : null,
              file_size: doc.file_size || null,
            };

            return (
              <div key={doc.id}>
                <AttachmentThumbnail
                  attachment={attachment}
                  onClick={() => handleView(doc)}
                  onDelete={() => setDeleteDocId(doc.id)}
                  className="w-[84px] h-[84px]"
                />
                <div className="w-[84px] mt-1">
                  <Badge variant="outline" className="text-[8px] h-3.5 px-1">
                    {doc.document_types?.name || 'Sem tipo'}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ===== MODO LISTA (Detalhado) ===== */
        <div className="space-y-1">
          {documents.map((doc) => {
            const publicUrl = getPublicUrl(doc.storage_path) || '';
            const fileType = mimeToFileType(doc.mime_type);
            const FileIcon = getFileIcon(fileType);
            const colorClasses = getFileColor(fileType);

            return (
              <div
                key={doc.id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-background hover:bg-muted/50 transition-colors group"
              >
                {/* Ícone do tipo de arquivo */}
                <div className={`p-1.5 rounded ${colorClasses} shrink-0`}>
                  <FileIcon className="h-4 w-4" />
                </div>

                {/* Nome do arquivo */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.file_name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[9px] h-4 px-1">
                      {doc.document_types?.name || 'Sem tipo'}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(doc.created_at).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {formatFileSize(doc.file_size)}
                    </span>
                  </div>
                </div>

                {/* Botões de ação */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleView(doc)}
                    title="Visualizar"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  <a
                    href={publicUrl}
                    download={doc.file_name}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-accent transition-colors"
                    title="Download"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => setDeleteDocId(doc.id)}
                    title="Remover"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox para preview interno */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={lightboxItems}
        initialIndex={lightboxIndex}
      />

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={!!deleteDocId} onOpenChange={(v) => !v && setDeleteDocId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover documento?</AlertDialogTitle>
            <AlertDialogDescription>
              O documento será marcado como removido mas permanecerá no histórico
              de contratos já gerados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
